// Record-player arm interaction; owns idle motion and pointer dragging.

type Point = {
	x: number
	y: number
}

const ARM_BASE_TRANSLATE_X = 280
const ARM_BASE_TRANSLATE_Y = 24
const ARM_PIVOT_X = 25
const ARM_PIVOT_Y = 25
const ARM_MIN_DEG = 8
const ARM_MAX_DEG = 42
const ARM_IDLE_MIN_DEG = 17
const ARM_IDLE_MAX_DEG = 32
const ARM_IDLE_PERIOD_MS = 40000
const ARM_DRAG_HIT_RADIUS_PX = 140

const ARM_RANGE_DEG = ARM_IDLE_MAX_DEG - ARM_IDLE_MIN_DEG
const ARM_HALF_PERIOD_MS = ARM_IDLE_PERIOD_MS / 2

export function initRecordPlayerArm(rootEl: HTMLElement): () => void {
	const svg = rootEl.querySelector<SVGSVGElement>('#record-player')
	const armBase = svg?.querySelector<SVGGElement>('#armbase')
	if (!svg || !armBase) return () => {}

	const arm = new RecordPlayerArm(svg, armBase)
	arm.init()
	return () => arm.dispose()
}

class RecordPlayerArm {
	private draggingPointerId: number | null = null
	private currentArmDeg = ARM_IDLE_MIN_DEG
	private dragStartPointerDeg = 0
	private dragStartArmDeg = this.currentArmDeg
	private lastDragDeltaDeg = 0
	private idlePhaseOffsetMs = 0
	private idleRafId: number | null = null
	private dragPivot: Point

	/** Captures SVG elements and binds record-arm handlers. */
	constructor(
		private readonly svg: SVGSVGElement,
		private readonly armBase: SVGGElement,
	) {
		this.dragPivot = this.getPivotScreenPoint()
		this.handlePointerDown = this.handlePointerDown.bind(this)
		this.handlePointerMove = this.handlePointerMove.bind(this)
		this.stopDrag = this.stopDrag.bind(this)
		this.clearCursorWhenIdle = this.clearCursorWhenIdle.bind(this)
		this.runIdle = this.runIdle.bind(this)
	}

	/** Registers arm interaction events and starts idle movement. */
	init(): void {
		this.armBase.style.transformOrigin = `${ARM_PIVOT_X}px ${ARM_PIVOT_Y}px`
		this.svg.addEventListener('pointerdown', this.handlePointerDown)
		this.svg.addEventListener('pointermove', this.handlePointerMove)
		this.svg.addEventListener('pointerup', this.stopDrag)
		this.svg.addEventListener('pointercancel', this.stopDrag)
		this.svg.addEventListener('pointerleave', this.clearCursorWhenIdle)
		this.setArmAngle(ARM_IDLE_MIN_DEG)
		this.startIdle()
	}

	/** Starts arm dragging when the pointer is within the hit radius. */
	dispose(): void {
		this.stopIdle()
		this.svg.removeEventListener('pointerdown', this.handlePointerDown)
		this.svg.removeEventListener('pointermove', this.handlePointerMove)
		this.svg.removeEventListener('pointerup', this.stopDrag)
		this.svg.removeEventListener('pointercancel', this.stopDrag)
		this.svg.removeEventListener('pointerleave', this.clearCursorWhenIdle)
	}

	private handlePointerDown(event: PointerEvent): void {
		const pivot = this.getPivotScreenPoint()
		if (!this.isInsideDragHitRadius(event, pivot)) return

		this.stopIdle()
		this.dragPivot = pivot
		this.draggingPointerId = event.pointerId
		this.svg.setPointerCapture(event.pointerId)
		this.armBase.classList.add('is-dragging')
		this.svg.style.cursor = 'grabbing'
		this.dragStartPointerDeg = this.getPointerPolarDeg(event, pivot)
		this.dragStartArmDeg = this.currentArmDeg
		this.lastDragDeltaDeg = 0
		this.updateFromPointer(event)
		event.preventDefault()
	}

	/** Updates drag position or hover cursor from pointer movement. */
	private handlePointerMove(event: PointerEvent): void {
		if (this.draggingPointerId === event.pointerId) {
			this.updateFromPointer(event)
			event.preventDefault()
			return
		}

		const pivot = this.getPivotScreenPoint()
		this.svg.style.cursor = this.isInsideDragHitRadius(event, pivot)
			? 'grab'
			: ''
	}

	/** Ends active dragging and resumes idle movement. */
	private stopDrag(event: PointerEvent): void {
		if (this.draggingPointerId !== event.pointerId) return
		if (this.svg.hasPointerCapture(event.pointerId)) {
			this.svg.releasePointerCapture(event.pointerId)
		}

		this.armBase.classList.remove('is-dragging')
		this.draggingPointerId = null
		this.svg.style.cursor = ''
		this.resumeIdleFromCurrentAngle()
	}

	/** Clears hover cursor when the pointer leaves outside a drag. */
	private clearCursorWhenIdle(): void {
		if (this.draggingPointerId === null) this.svg.style.cursor = ''
	}

	/** Applies pointer movement as a bounded arm rotation. */
	private updateFromPointer(event: PointerEvent): void {
		const pointerDeg = this.getPointerPolarDeg(event, this.dragPivot)
		const deltaDeg = normalizeDeltaDeg(
			pointerDeg - this.dragStartPointerDeg,
		)
		this.lastDragDeltaDeg = deltaDeg
		this.setArmAngle(this.dragStartArmDeg + deltaDeg)
	}

	/** Applies the current arm rotation transform. */
	private setArmAngle(deg: number): void {
		const boundedDeg = clamp(deg, ARM_MIN_DEG, ARM_MAX_DEG)
		this.currentArmDeg = boundedDeg
		this.armBase.style.transform = `translate(${ARM_BASE_TRANSLATE_X}px, ${ARM_BASE_TRANSLATE_Y}px) rotate(${boundedDeg}deg)`
	}

	/** Converts the SVG arm pivot to screen coordinates. */
	private getPivotScreenPoint(): Point {
		const rect = this.svg.getBoundingClientRect()
		const viewBox = this.svg.viewBox.baseVal
		const scaleX = rect.width / viewBox.width
		const scaleY = rect.height / viewBox.height
		return {
			x: rect.left + (ARM_BASE_TRANSLATE_X + ARM_PIVOT_X) * scaleX,
			y: rect.top + (ARM_BASE_TRANSLATE_Y + ARM_PIVOT_Y) * scaleY,
		}
	}

	/** Returns the pointer angle around the current arm pivot. */
	private getPointerPolarDeg(event: PointerEvent, pivot: Point): number {
		return (
			(Math.atan2(event.clientY - pivot.y, event.clientX - pivot.x) *
				180) /
			Math.PI
		)
	}

	/** Checks whether the pointer can grab the arm. */
	private isInsideDragHitRadius(event: PointerEvent, pivot: Point): boolean {
		return (
			Math.hypot(event.clientX - pivot.x, event.clientY - pivot.y) <=
			ARM_DRAG_HIT_RADIUS_PX
		)
	}

	/** Advances idle motion while the user is not dragging. */
	private runIdle(nowMs: number): void {
		if (this.draggingPointerId !== null) return
		this.setArmAngle(this.getIdleAngleAtTime(nowMs))
		this.idleRafId = window.requestAnimationFrame(this.runIdle)
	}

	/** Starts the idle animation loop. */
	private startIdle(): void {
		if (this.idleRafId === null) {
			this.idleRafId = window.requestAnimationFrame(this.runIdle)
		}
	}

	/** Stops the idle animation loop. */
	private stopIdle(): void {
		if (this.idleRafId === null) return
		window.cancelAnimationFrame(this.idleRafId)
		this.idleRafId = null
	}

	/** Resumes idle motion from the released arm angle. */
	private resumeIdleFromCurrentAngle(): void {
		const clamped = clamp(
			this.currentArmDeg,
			ARM_IDLE_MIN_DEG,
			ARM_IDLE_MAX_DEG,
		)
		const progress =
			ARM_RANGE_DEG === 0
				? 0
				: (clamped - ARM_IDLE_MIN_DEG) / ARM_RANGE_DEG
		const cycleAtRelease =
			this.lastDragDeltaDeg >= 0
				? progress * ARM_HALF_PERIOD_MS
				: ARM_HALF_PERIOD_MS + (1 - progress) * ARM_HALF_PERIOD_MS

		this.idlePhaseOffsetMs =
			cycleAtRelease - (performance.now() % ARM_IDLE_PERIOD_MS)
		this.startIdle()
	}

	/** Computes the idle arm angle for the current animation time. */
	private getIdleAngleAtTime(nowMs: number): number {
		const cycle =
			(((nowMs + this.idlePhaseOffsetMs) % ARM_IDLE_PERIOD_MS) +
				ARM_IDLE_PERIOD_MS) %
			ARM_IDLE_PERIOD_MS

		if (cycle <= ARM_HALF_PERIOD_MS) {
			return (
				ARM_IDLE_MIN_DEG + (ARM_RANGE_DEG * cycle) / ARM_HALF_PERIOD_MS
			)
		}

		return (
			ARM_IDLE_MAX_DEG -
			(ARM_RANGE_DEG * (cycle - ARM_HALF_PERIOD_MS)) / ARM_HALF_PERIOD_MS
		)
	}
}

function normalizeDeltaDeg(deg: number): number {
	return ((deg + 540) % 360) - 180
}

function clamp(value: number, min: number, max: number): number {
	return Math.max(min, Math.min(max, value))
}
